import {
  asText,
  Content,
  filter,
  LinkField,
  RichTextField,
} from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  HeroCardHeaderSubpagesSliceBlogHeaderCarousel,
  HeroCardHeaderSubpagesSliceDefault,
} from '../../../prismicio-types';
import { createClient } from '@/prismicio';
import { blogCardQuery } from '@/components/blogHome/constants';
import { Carousel } from '@/components';

enum SLICE_VARIATION {
  default = 'default',
  blogHeaderCarousel = 'blogHeaderCarousel',
}

/**
 * Props for `HeroCardHeaderSubpages`.
 */
export type HeroCardHeaderSubpagesProps =
  SliceComponentProps<Content.HeroCardHeaderSubpagesSlice> & {
    context?: { pageName: string };
  };

/**
 * Component for "HeroCardHeaderSubpages" Slices.
 */
const HeroCardHeaderSubpages = ({
  slice,
  context,
}: HeroCardHeaderSubpagesProps): JSX.Element => {
  let sectionClassName = 'py-10 max-w-screen-2xl mx-auto';

  if (slice.variation === SLICE_VARIATION.blogHeaderCarousel) {
    sectionClassName = 'pt-20 pb-2 background';
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`text-white px-5 md:px-16 ${sectionClassName}`}
    >
      {slice.variation === SLICE_VARIATION.default && (
        <DefaultVariation slice={slice} />
      )}

      {slice.variation === SLICE_VARIATION.blogHeaderCarousel && (
        <BlogHeaderVariation slice={slice} pageName={context!.pageName} />
      )}
    </section>
  );
};

const DefaultVariation = ({
  slice,
}: {
  slice: HeroCardHeaderSubpagesSliceDefault;
}) => {
  const {
    primary: { title, subtitle, image, button_label, button_link },
  } = slice;

  return (
    <div
      className={`flex flex-col lg:flex-row gap-4 items-center lg:items-start lg:justify-between relative`}
    >
      <div className="text-left w-full mb-12 lg:mb-0 z-10 lg:max-w-[600px]">
        <div className="mb-4">
          <PrismicRichText field={title} />
        </div>
        <PrismicRichText field={subtitle} />

        <div className="flex justify-start mt-8">
          <PrismicNextLink field={button_link} className="button-bordered">
            <span>{button_label}</span>
          </PrismicNextLink>
        </div>
      </div>
      <div className="-mr-5 md:-mr-16 lg:-mt-20 h-full lg:max-w-[50%]">
        <PrismicNextImage
          field={image}
          className="lg:h-[100%+4rem] object-cover"
        />
      </div>
    </div>
  );
};

const BlogHeaderVariation = async ({
  slice,
  pageName,
}: {
  slice: HeroCardHeaderSubpagesSliceBlogHeaderCarousel;
  pageName: string;
}) => {
  const {
    primary: { button_label, blog_posts_carousel, hidden_title },
  } = slice;
  const client = createClient();
  const featuredPosts = await client.getAllByType('blog', {
    orderings: [
      { field: 'document.first_publication_date', direction: 'desc' },
    ],
    filters: [filter.at('my.blog.featured', true)],
    limit: 3,
    graphQuery: blogCardQuery,
  });

  const allPosts = [
    ...featuredPosts.map((post) => ({
      post_title: post?.data?.card_title,
      post_description: [
        {
          type: 'paragraph',
          text: post?.data?.meta_description as string,
          spans: [] as never[],
        },
      ] as RichTextField,
      post_link: { link_type: 'Web', url: post.url } as LinkField,
      post_thumbail: post?.data?.image,
    })),
    ...blog_posts_carousel,
  ];

  return (
    <>
      {asText(hidden_title) && (
        <span className="hidden">
          <PrismicRichText field={hidden_title} />
        </span>
      )}
      <Carousel dotType="large" arrows={false}>
        {allPosts.map((post, index) => (
          <div key={index}>
            <div className="flex flex-col md:flex-row pb-20 mx-1">
              <div className="w-full md:w-1/2 text-center md:text-left">
                {pageName && (
                  <p className="text-base mb-6 hidden md:block">{pageName}</p>
                )}
                <div className="mb-8">
                  <p className="text-h1">{asText(post.post_title)}</p>
                </div>
                <div className="mb-10">
                  <PrismicRichText field={post.post_description} />
                </div>
                <PrismicNextLink
                  field={post.post_link}
                  className="button-bordered"
                >
                  <span>{button_label}</span>
                </PrismicNextLink>
              </div>
              <div className="hidden md:flex w-full md:w-1/2 md:ml-10 items-center justify-center ">
                <div className="overflow-hidden w-full max-h-[436px] rounded-xl">
                  <PrismicNextImage field={post.post_thumbail} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </Carousel>
    </>
  );
};

export default HeroCardHeaderSubpages;
